import React, { useEffect, useState } from 'react';
import { Alert, Button, Table } from 'react-bootstrap';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { getMissingContractClients, MissingContractClient } from '../../utils/invoice-text-templates';

type MissingContractClientsProps = {
    templateId: string;
    onBlocked: (blocked: boolean) => void;
};

export const MissingContractClients = ({
    templateId,
    onBlocked,
}: MissingContractClientsProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoiceTextTemplates' });
    const [items, setItems] = useState<MissingContractClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        onBlocked(true);
        getMissingContractClients(templateId)
            .then((rows) => {
                if (active) {
                    setItems(rows);
                    onBlocked(rows.length > 0);
                }
            })
            .catch(() => {
                if (active) setError('checkError');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [templateId, revision, onBlocked]);

    const headers = [t('clientId'), t('client'), t('eik'), t('contractNumber')];
    const rows = () => items.map((item) => [String(item.id), item.companyName, item.eik ?? '', '']);

    const copy = async () => {
        setBusy(true);
        setMessage('');
        // Prevent spreadsheet formulas from client-provided names; preserve leading zeros in EIK.
        const cell = (value: string, index: number) => {
            const clean = value.replace(/[\t\r\n]+/g, ' ');
            return (index === 2 && clean) || /^[=+@-]/.test(clean) ? "'" + clean : clean;
        };
        const text = [headers.join('\t'), ...rows().map((row) => row.map((value, index) => cell(value, index)).join('\t'))].join('\r\n');
        try {
            if (!navigator.clipboard?.writeText) {
                setMessage('copyError');
                return;
            }
            await navigator.clipboard.writeText(text);
            setMessage('copied');
        } catch {
            setMessage('copyError');
        } finally {
            setBusy(false);
        }
    };

    const exportExcel = async () => {
        setBusy(true);
        setMessage('');
        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(t('worksheet'));
            sheet.addRow(headers).font = { bold: true };
            rows().forEach((row) => sheet.addRow(row));
            sheet.columns.forEach((column, index) => {
                column.width = Math.min(
                    60,
                    Math.max(
                        headers[index].length + 2,
                        ...items.map((item) => {
                            let value = String(item.id);
                            if (index === 1) value = item.companyName;
                            else if (index === 2) value = item.eik ?? '';
                            return value.length + 2;
                        })
                    )
                );
                column.numFmt = '@';
            });
            sheet.views = [{ state: 'frozen', ySplit: 1 }];
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(
                new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                'missing-contract-numbers.xlsx'
            );
        } catch {
            setMessage('exportError');
        } finally {
            setBusy(false);
        }
    };
    if (!loading && !error && items.length === 0) return null;

    return (
        <div className='mt-3'>
            {!loading && <Button
                type='button'
                className='mb-2'
                disabled={loading || busy}
                onClick={() => setRevision((value) => value + 1)}
            >
                {t('recheck')}
            </Button>}
            {loading && <p>{t('checking')}</p>}
            {!loading && error && <Alert variant='danger'>{t(error)}</Alert>}
            {!loading && !error && (
                <>
                    <Alert variant='warning'>
                        {t('missingClients')}: <strong>{items.length}</strong>.
                        {items.length > 0 && <> {t('blocked')}</>}
                    </Alert>
                    {items.length > 0 && (
                        <>
                            <Button type='button' disabled={busy} onClick={copy}>
                                {t('copy')}
                            </Button>{' '}
                            <Button type='button' disabled={busy} onClick={exportExcel}>
                                {t(busy ? 'processing' : 'export')}
                            </Button>
                            <div className='overflow-auto mt-2' style={{ maxHeight: 300 }}>
                                <Table size='sm'>
                                    <thead>
                                        <tr>
                                            {headers.slice(0, 3).map((header) => (
                                                <th key={header}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.id}</td>
                                                <td>{item.companyName}</td>
                                                <td>{item.eik}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </>
            )}
            {message && (
                <Alert variant='info' className='mt-2'>
                    {t(message)}
                </Alert>
            )}
        </div>
    );
};
